import { getTsCustomerPortalClient } from '@montugroup/circuit-shared/api/customer-portal.client';
import useMutationAsQuery from '@montugroup/circuit-shared/hooks/useMutationAsQuery';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { create } from 'zustand';

const client = getTsCustomerPortalClient();

type SimpleProductSelection = { id: number; quantity: number };

const RECOMMENDED_PRODUCTS_LIMIT = 3;

const recommendedProductsInput = (products: SimpleProductSelection[]) => ({
  body: {
    data: {
      productIds: products.map(({ id }) => id),
      limit: RECOMMENDED_PRODUCTS_LIMIT,
    },
  },
});

interface CrossSellStore {
  initialSelection: SimpleProductSelection[];
  showCrossSellPage: boolean;
  crossSellCausedSelectionChange: boolean;
  setInitialSelection: (selection: SimpleProductSelection[]) => void;
  setShowCrossSellPage: (show: boolean) => void;
  setCrossSellCausedSelectionChange: (changed: boolean) => void;
  reset: () => void;
}

const useCrossSellStore = create<CrossSellStore>((set) => ({
  initialSelection: [],
  showCrossSellPage: false,
  crossSellCausedSelectionChange: false,
  setInitialSelection: (initialSelection) => set({ initialSelection }),
  setShowCrossSellPage: (showCrossSellPage) => set({ showCrossSellPage }),
  setCrossSellCausedSelectionChange: (crossSellCausedSelectionChange) => set({ crossSellCausedSelectionChange }),
  reset: () => set({ initialSelection: [], showCrossSellPage: false, crossSellCausedSelectionChange: false }),
}));

export const useCrossSell = (selectedProducts?: SimpleProductSelection[]) => {
  const {
    initialSelection,
    showCrossSellPage,
    crossSellCausedSelectionChange,
    setInitialSelection,
    setShowCrossSellPage,
    setCrossSellCausedSelectionChange,
    reset,
  } = useCrossSellStore();

  const queryClient = useQueryClient();

  const { mutateAsync: listRecommendedProducts } = client.listRecommendedProducts.useMutation();
  const { data } = useMutationAsQuery(client.listRecommendedProducts, recommendedProductsInput(initialSelection), {
    enabled: showCrossSellPage,
    staleTime: Infinity,
    retry: false,
  });

  const crossSellableProducts = data?.body.data.products;
  const impressionId = data?.body.data.impressionId;

  const attemptCrossSell = async () => {
    if (selectedProducts) {
      try {
        const input = recommendedProductsInput(selectedProducts);
        const response = await queryClient.fetchQuery({
          queryKey: [...client.listRecommendedProducts.keys, input],
          queryFn: () => listRecommendedProducts(input),
        });
        if (response.body.data.products.length === 0) {
          return false;
        }
      } catch {
        return false;
      }

      setInitialSelection(selectedProducts);
      setShowCrossSellPage(true);
      return true;
    }
  };

  const resetCrossSell = useCallback(() => {
    reset();
  }, [reset]);

  // Only calculate if selectedProducts is provided
  const calculatedChange = useMemo(() => {
    if (!selectedProducts || !crossSellableProducts || !showCrossSellPage) {
      return false;
    }

    return crossSellableProducts.some((crossSellableProduct) => {
      const currentQuantity = selectedProducts.find((p) => p.id === crossSellableProduct.id)?.quantity;
      const initialQuantity = initialSelection.find((p) => p.id === crossSellableProduct.id)?.quantity;
      return initialQuantity !== currentQuantity;
    });
  }, [crossSellableProducts, initialSelection, selectedProducts, showCrossSellPage]);

  useEffect(() => {
    if (selectedProducts && calculatedChange !== crossSellCausedSelectionChange) {
      setCrossSellCausedSelectionChange(calculatedChange);
    }
  }, [calculatedChange, selectedProducts, crossSellCausedSelectionChange, setCrossSellCausedSelectionChange]);

  const getRecommendation = (position: number) => (impressionId ? { impressionId, position } : undefined);

  return {
    crossSellCausedSelectionChange,
    attemptCrossSell,
    showCrossSellPage,
    resetCrossSell,
    crossSellableProducts,
    getRecommendation,
  };
};
